import { useCallback, useLayoutEffect, useRef, useState, Suspense, lazy, type ReactElement } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDate, formatMoney } from "@bw/shared/utils";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { Link, useSearchParams } from "react-router-dom";
import type { FinanceDisputeResponse, FinanceSessionPurpose } from "@bw/shared/types/generated";
import { useMyOrg } from "@/hooks/use-my-org";
import { useFinanceContext } from "@/hooks/use-finance-context";
import { FinanceAccountSetup } from "./FinanceAccountSetup";
import { FinanceReports } from "./FinanceReports";
import { PayoutRecoveryPane } from "./PayoutRecoveryPane";
import type { FinanceApi } from "@bw/shared/api";

export interface PaymentsWorkspaceProps {
  api: FinanceApi;
}

const Embed = lazy(() => import("@/components/finance-connect/FinanceConnectEmbed").then(module => ({ default: module.FinanceConnectEmbed })));

export function PaymentsWorkspace({ api }: PaymentsWorkspaceProps): ReactElement {
  const cache = useQueryClient();
  const [search, setSearch] = useSearchParams();
  const legacyRunLink = !search.has("tab") && search.has("payout_pull_run");
  const reportsView = search.get("tab") === "reports" || legacyRunLink;
  useLayoutEffect(() => {
    if (!legacyRunLink) return;
    const next = new URLSearchParams(search);
    next.set("tab", "reports");
    setSearch(next, { replace: true });
  }, [legacyRunLink, search, setSearch]);
  const org = useMyOrg();
  const orgId = org.org?.org_id ?? null;
  const { context, actorScope, dataKey, pending, readiness, refreshAccount } = useFinanceContext(orgId, api);
  const [purpose, setPurpose] = useState<FinanceSessionPurpose | null>(null);
  const [cursor, setCursor] = useState<string | undefined>();
  const [selected, setSelected] = useState<FinanceDisputeResponse | null>(null);
  const [error, setError] = useState("");
  const disputeRead = Boolean(!reportsView && context?.account_bound && context.allowed_actions.includes("org:finance_disputes") &&
    context.stripe_configuration.state === "configured");
  const scopeStamp = JSON.stringify([dataKey, disputeRead, reportsView]);
  const currentScope = useRef({ stamp: scopeStamp });
  if (currentScope.current.stamp !== scopeStamp) currentScope.current = { stamp: scopeStamp };
  useLayoutEffect(() => { setPurpose(null); setCursor(undefined); setSelected(null); setError(""); }, [scopeStamp]);
  const disputes = useQuery({ queryKey: [...dataKey, "disputes", cursor],
    queryFn: ({ signal }) => api.listDisputes(orgId as string, { cursor, limit: 25 }, signal), enabled: disputeRead, retry: false });
  const refresh = useCallback(async () => {
    if (!orgId) return;
    setError("");
    try { await refreshAccount(); if (disputeRead) await disputes.refetch(); }
    catch { setError("Stripe status is unavailable. Your recorded reports are still readable."); }
  }, [api, orgId, refreshAccount, disputes.refetch, disputeRead]);
  const fetchSession = useCallback(() => purpose === "onboarding" ? api.createOnboardingSession(orgId as string) :
    api.createAccountSession(orgId as string, { purpose: purpose as "account_settings" | "disputes" }), [api, orgId, purpose]);
  if (org.kind === "loading" || pending) return <CircularProgress aria-label="Loading payments" size={24} />;
  if (!orgId || !context || !actorScope) return <Alert severity="error">Payments access is unavailable. Select an organization or refresh.</Alert>;
  const accountStatus = readiness?.ready === true
    ? { label: "Ready", color: "success" as const }
    : readiness?.ready === false
      ? { label: "Action needed", color: "warning" as const }
      : context.account_bound
        ? { label: "Connected", color: "primary" as const }
        : { label: "Not connected", color: "default" as const };
  const accountSummary = readiness?.ready === true
    ? "Your payment account is ready to accept charges and receive payouts."
    : readiness?.ready === false
      ? "Stripe needs more information before every payment capability is ready."
      : context.account_bound
        ? "Your payment account is connected. Refresh it to confirm the latest readiness."
        : "Connect a company payment account before accepting online payments.";
  return <Stack spacing={3} sx={{ width: "100%", maxWidth: 1120, mx: "auto", px: { xs: 2, sm: 3 }, pb: 4 }}>
    <Stack spacing={0.5}>
      <Box>
        <Typography component="h1" variant="h4" sx={{ fontWeight: 750 }}>Payments</Typography>
        <Typography color="text.secondary" variant="body2">
          Manage your payment account, disputes, reports, and payout recovery in one place.
        </Typography>
      </Box>
    </Stack>
    {error && <Alert severity="error">{error}</Alert>}
    {!reportsView && <>
    <Paper component="section" variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }}><Stack spacing={2}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "flex-start" }}>
        <Box sx={{ display: "grid", placeItems: "center", width: 42, height: 42, borderRadius: 2,
          bgcolor: "action.hover", color: "primary.main", flex: "0 0 auto" }}>
          <AccountBalanceRoundedIcon aria-hidden="true" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
            <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>Payment account</Typography>
            <Chip color={accountStatus.color} label={accountStatus.label} size="small" />
          </Stack>
          <Typography color="text.secondary" variant="body2">{accountSummary}</Typography>
        </Box>
      </Stack>
      {readiness && <Typography color="text.secondary" variant="body2">Checked {formatDate(readiness.provider_checked_at)}.
        {readiness.requirements.currently_due_count > 0 ? ` ${readiness.requirements.currently_due_count} requirements currently due.` : " No outstanding requirements were reported."}</Typography>}
      {context.stripe_configuration.state === "unavailable" && <Alert severity="info">Stripe controls are unavailable. Your recorded reports remain available in the Reports tab.</Alert>}
      <Divider />
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
        {context.allowed_session_purposes.includes("account_settings") && <Button size="small" variant="outlined" onClick={() => setPurpose("account_settings")}>Bank and account settings</Button>}
        {context.allowed_session_purposes.includes("disputes") && <Button size="small" variant="outlined" onClick={() => setPurpose("disputes")}>Manage disputes</Button>}
        {context.account_bound && context.stripe_configuration.state === "configured" && <Button size="small" onClick={() => void refresh()}>Refresh status</Button>}
        {context.can_manage_finance_access && <Button component={Link} to="/staff?tab=people" size="small">Manage finance access</Button>}
      </Stack>
      {(!context.account_bound || context.allowed_session_purposes.includes("onboarding")) &&
        <FinanceAccountSetup orgId={orgId} api={api} showSummary={false} />}
    </Stack></Paper>
    {purpose && context.allowed_session_purposes.includes(purpose) && <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }}>
      <Stack direction="row" sx={{ justifyContent: "space-between", mb: 1 }}><Typography component="h2" variant="h6">
        {purpose === "disputes" ? "Dispute management" : "Account maintenance"}</Typography><Button size="small" onClick={() => setPurpose(null)}>Close</Button></Stack>
      <Suspense fallback={<CircularProgress aria-label="Loading Stripe" size={24} />}><Embed actorScope={actorScope} context={context} purpose={purpose}
        fetchSession={fetchSession} onExit={() => setPurpose(null)} onRefresh={refresh} /></Suspense>
    </Paper>}
    {disputeRead && <Paper component="section" variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }}><Stack spacing={1.5}>
      <Stack direction="row" spacing={1.25} sx={{ alignItems: "center" }}>
        <ReportProblemOutlinedIcon aria-hidden="true" color="action" />
        <Box>
          <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>Disputes</Typography>
          <Typography color="text.secondary" variant="body2">Review payment disputes and response deadlines.</Typography>
        </Box>
      </Stack>
      {disputes.isPending ? <CircularProgress aria-label="Loading disputes" size={20} /> : disputes.isError ? <Alert severity="error">Disputes are unavailable.</Alert> :
        disputes.data?.items.length ? disputes.data.items.map(row => <Stack key={row.dispute_id} direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ justifyContent: "space-between" }}>
          <Typography variant="body2">{formatMoney(row.amount, row.currency)} · {row.status.replaceAll("_", " ")}
            {row.evidence_due_by ? ` · Evidence due ${formatDate(row.evidence_due_by)}` : " · No response deadline"}</Typography>
          <Button size="small" onClick={() => { const started = currentScope.current;
            void api.getDispute(orgId, row.dispute_id).then(result => {
              if (currentScope.current === started && result.org_id === orgId) setSelected(result);
            }).catch(() => { if (currentScope.current === started) setError("Dispute detail is unavailable."); }); }}>Details</Button>
        </Stack>) : <Typography variant="body2">No disputes reported.</Typography>}
      {disputes.data?.has_more && disputes.data.next_cursor && <Button size="small" onClick={() => setCursor(disputes.data?.next_cursor ?? undefined)}>Next disputes</Button>}
      {cursor && <Button size="small" onClick={() => setCursor(undefined)}>First page</Button>}
    </Stack></Paper>}
    </>}
    {reportsView && <FinanceReports orgId={orgId} api={api} scope={dataKey} enabled={context.allowed_actions.includes("org:finance_read")} />}
    {/* Keep unresolved recovery intent mounted across tabs so retries retain their key. */}
    <Paper variant="outlined" hidden={!reportsView} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, display: reportsView ? "block" : "none" }}><PayoutRecoveryPane orgId={orgId} api={api} context={context} actorScope={actorScope}
      initialRunId={search.get("payout_pull_run")} onRunChanged={runId => {
        const next = new URLSearchParams(search);
        if (runId === null) next.delete("payout_pull_run"); else next.set("payout_pull_run", runId);
        setSearch(next);
      }}
      onLedgerChanged={async () => { await Promise.all(["settlements", "payouts", "reconciliation"].map(report =>
        cache.invalidateQueries({ queryKey: [...dataKey, report] }))); }} /></Paper>
    <Dialog open={disputeRead && Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth="sm"><DialogTitle>Dispute detail</DialogTitle><DialogContent>
      {selected && <Stack spacing={1}><Typography>{formatMoney(selected.amount, selected.currency)} · {selected.status.replaceAll("_", " ")}</Typography>
        <Typography>Reason: {selected.reason_code.replaceAll("_", " ")}</Typography><Typography>{selected.evidence_due_by ? `Evidence due ${formatDate(selected.evidence_due_by)}` : "No response deadline."}</Typography>
        <Typography>{selected.source.source_state === "available" ? "Recorded BW charge linked." : "Charge association unavailable."}</Typography>
        <Typography>{selected.has_evidence ? "Evidence is staged in Stripe." : "No evidence staged."} Submissions: {selected.evidence_submission_count}.</Typography>
        <Typography variant="body2">Open dispute management to review charge details or submit evidence. Opening it does not submit evidence.</Typography></Stack>}
    </DialogContent><DialogActions><Button size="small" onClick={() => setSelected(null)}>Close</Button></DialogActions></Dialog>
  </Stack>;
}
